import { ScheduleSubModes } from "@common/helpers/domains/accounting/accruedScheduleMode";
import { ScheduleRecognitionOverrides } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import {
    RecognitionAnchor,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import {
    getScheduleApplyTermStart,
    ScheduleAccruedBilling,
} from "../scheduleApplyAccruedHelpers";
import { ResolvedScheduleDraft, ScheduleDraft } from "./scheduleDraft";

/** What a source needs before the schedule modal can apply a template to it. */
export interface ScheduleSpecificDateApplyRequest {
    templateId: string;
    templateName: string;
    entityId: number;
    sourceType:
        | ScheduleSourceType.invoice
        | ScheduleSourceType.bill
        | ScheduleSourceType.transaction;
    amount: number;
    sourceLabel: string;
    /** Identifies the source (invoice and lines, or transaction) so a new request re-seeds the modal. */
    sourceKey: string;
    /** The source's own calendar day; rows not on the specific date start from it. */
    sourceDate: string | null;
    /** The billing an accrued ladder is checked against, or null when the source has no date. */
    billing: ScheduleAccruedBilling | null;
    /** The account of each scheduled bill line; the schedule recognizes into it when they all share one. */
    sourceLineAccountIds?: Array<string | null>;
}

export interface ScheduleSpecificDateApplyResult {
    /** Set when a row is anchored on the specific date. */
    specificDate?: string;
    termStart?: string;
    overrides?: ScheduleRecognitionOverrides;
}

/**
 * The start the backend uses when none is sent. An accrued start runs back
 * from the source by the term the backend receives, which is the draft's once
 * the modal edits it, so the template's own term cannot stand in for it.
 */
export function getSpecificDateDefaultTermStart(options: {
    template: Partial<ScheduleSubModes> | undefined;
    term: Pick<ScheduleDraft, "termLength" | "termUnit"> | null;
    sourceDate: string | null;
}): string {
    const { template, term, sourceDate } = options;
    if (!sourceDate) {
        return "";
    }
    return getScheduleApplyTermStart({
        template:
            template && term
                ? {
                      revenueSubMode: template.revenueSubMode,
                      expenseSubMode: template.expenseSubMode,
                      termLength: term.termLength,
                      termUnit: term.termUnit,
                  }
                : undefined,
        sourceDate,
    });
}

/**
 * The apply the schedule modal submits, or null while a row still needs the
 * specific date. A start moved off the default travels with the rows: the
 * backend reads `termStart` only for rows anchored on it, and the draft
 * anchors every non-specific row there.
 */
export function resolveSpecificDateApply(options: {
    resolved: Pick<
        ResolvedScheduleDraft,
        "specificDate" | "termStart" | "components"
    > | null;
    overrides: ScheduleRecognitionOverrides;
    /** The start the backend would use on its own for the draft's term. */
    defaultTermStart: string;
}): ScheduleSpecificDateApplyResult | null {
    const { resolved, defaultTermStart } = options;
    if (!resolved) {
        return null;
    }
    const isSpecificDateNeeded = resolved.components.some(
        (component) => component.anchor === RecognitionAnchor.specificDate,
    );
    if (isSpecificDateNeeded && !resolved.specificDate) {
        return null;
    }
    const isStartMoved =
        resolved.termStart !== null && resolved.termStart !== defaultTermStart;
    const overrides = isStartMoved
        ? { ...options.overrides, components: resolved.components }
        : options.overrides;
    return {
        ...(resolved.specificDate
            ? { specificDate: resolved.specificDate }
            : {}),
        ...(isStartMoved && resolved.termStart
            ? { termStart: resolved.termStart }
            : {}),
        ...(Object.keys(overrides).length > 0 ? { overrides } : {}),
    };
}
